'use client';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { useState } from 'react';
import { AuthProvider } from '@/components/auth/AuthProvider';
import { StatusBarInit } from '@/components/StatusBarInit';
import { createIDBPersister, clearQueryCache as clearIDBCache } from '@/lib/persister';

// 导出清理缓存的函数，供登出时使用
export { clearIDBCache as clearQueryCache };

// 创建持久化器：优先使用 IndexedDB，降级到 localStorage
let persister: ReturnType<typeof createIDBPersister> | ReturnType<typeof createSyncStoragePersister> | null = null;

if (typeof window !== 'undefined') {
  try {
    // 优先使用 IndexedDB（支持更大数据，更可靠）
    persister = createIDBPersister();
  } catch (error) {
    console.warn('[PWA] IndexedDB not available, falling back to localStorage:', error);
    // 降级到 localStorage
    try {
      persister = createSyncStoragePersister({
        storage: window.localStorage,
        key: 'REACT_QUERY_OFFLINE_CACHE',
      });
    } catch (localStorageError) {
      console.error('[PWA] localStorage also not available:', localStorageError);
      persister = null;
    }
  }
}

export function Providers({ children }: { children: React.ReactNode }) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            // 增加缓存时间到5分钟，减少查询频率
            staleTime: 5 * 60 * 1000, // 5 minutes - 数据保持新鲜的时间
            // 缓存保留时间延长到7天，支持离线查看
            gcTime: 7 * 24 * 60 * 60 * 1000, // 7 days - 缓存保留时间（支持离线查看）
            // 禁用窗口聚焦时重新获取，避免不必要的请求
            refetchOnWindowFocus: false,
            // 禁用组件挂载时重新获取，使用缓存数据
            refetchOnMount: false,
            // 网络重连时重新获取（离线恢复后自动刷新）
            refetchOnReconnect: true,
            // 失败时重试2次，使用指数退避策略
            retry: 2,
            retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
          },
        },
      })
  );

  // 只在客户端且 persister 可用时启用持久化
  const shouldPersist = typeof window !== 'undefined' && persister !== null;

  if (shouldPersist) {
    return (
      <PersistQueryClientProvider
        client={queryClient}
        persistOptions={{
          persister: persister!,
          maxAge: 7 * 24 * 60 * 60 * 1000, // 7 days
          buster: 'v1', // 版本标识，更新此值可强制清除旧缓存
        }}
      >
        <StatusBarInit />
        <AuthProvider>{children}</AuthProvider>
      </PersistQueryClientProvider>
    );
  }

  // 降级：不使用持久化
  return (
    <QueryClientProvider client={queryClient}>
      <StatusBarInit />
      <AuthProvider>{children}</AuthProvider>
    </QueryClientProvider>
  );
}

